type GraphQLRequestOptions = {
  query: string
  variables?: Record<string, any>
  method?: 'POST' | 'GET'
  headers?: Record<string, string>
}

class GraphQLClient {
  private url: string
  private defaultHeaders: Record<string, string>

  constructor({ url, apiKey, idToken }: { url: string; apiKey?: string; idToken?: string }) {
    this.url = url
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...(apiKey && { 'x-api-key': apiKey }),
      ...(idToken && { Authorization: `Bearer ${idToken}` })
    }
  }

  async request<T>(
    { query, variables, method = 'POST', headers }: GraphQLRequestOptions,
    retryCount = 3,
    retryDelay = 500 // milliseconds
  ): Promise<T> {
    let lastError: unknown = null

    for (let attempt = 0; attempt <= retryCount; attempt++) {
      try {
        const response = await fetch(this.url, {
          method,
          headers: {
            ...this.defaultHeaders,
            ...headers
          },
          body: JSON.stringify({ query, variables })
        })

        const result = await response.json()

        if (!response.ok || (Array.isArray(result.errors) && result.errors.length > 0)) {
          throw new Error(result.errors?.[0]?.message || 'GraphQL request failed')
        }

        return result.data
      } catch (error) {
        lastError = error
        const isLastAttempt = attempt === retryCount

        if (!isLastAttempt) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay))
        }
      }
    }

    // Throw only once after all retries have failed
    throw lastError
  }
}

export default GraphQLClient
